// ruta: contract-manager/client/pages/access-control/settings/index.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { Switch, Select, Option, Button, Checkbox, Textarea, Typography } from '@material-tailwind/react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { PageLayout } from '@/layouts/page-layout'
import { toast } from 'react-toastify'

const NOTIF_SUBJECTS = [
  { value: 'employee', label: 'Persona' },
  { value: 'asset', label: 'Activo' }
]

const NOTIF_TARGETS = [
  { key: 'mandante', label: 'Mandante' },
  { key: 'contratista', label: 'Contratista' },
  { key: 'contacto_contratista', label: 'Contacto Contratista' },
  { key: 'prevencionista', label: 'Prevencionista de riesgos' },
  { key: 'representante_legal', label: 'Representante Legal' },
  { key: 'usuario_principal', label: 'Usuario Principal' },
  { key: 'administrador_contrato', label: 'Administrador de contrato' }
]

export function AccessControlSettings() {
  const [groupedSettings, setGroupedSettings] = useState([])
  const [activeSubject, setActiveSubject] = useState(null)

  // --- Notificaciones (nueva sección) ---
  const [notifRules, setNotifRules] = useState({ employee: null, asset: null })
  const [notifEvent, setNotifEvent] = useState('access_reject')
  const [notifLoading, setNotifLoading] = useState(true)
  const [notifSaving, setNotifSaving] = useState(false)
  // Dirty flags por tipo para evitar postear tarjetas no modificadas
  const [notifDirty, setNotifDirty] = useState({ employee: false, asset: false })

  const navigate = useNavigate()

  useEffect(() => {
    axios.get('/api/v1/access_control_settings').then((res) => {
      const groups = res.data.access_control_settings || []
      setGroupedSettings(groups)
      if (groups[0]?.subject) setActiveSubject(groups[0].subject)
    })
  }, [])

  // Carga reglas de notificación
  useEffect(() => {
    const fetchNotif = async () => {
      setNotifLoading(true)
      try {
        const { data } = await axios.get('/api/v1/access_control/notification_rules', { params: { event: notifEvent } })
        setNotifRules({
          employee: data?.rules?.employee || null,
          asset: data?.rules?.asset || null
        })
        // Al refrescar desde servidor, nada está “dirty”
        setNotifDirty({ employee: false, asset: false })
      } catch {
        toast.error('No se pudieron cargar las reglas de notificación')
      } finally {
        setNotifLoading(false)
      }
    }
    fetchNotif()
  }, [notifEvent])

  const groupedBySubject = useMemo(() => {
    return groupedSettings.reduce((acc, group, index) => {
      if (!acc[group.subject]) acc[group.subject] = []
      acc[group.subject].push({ ...group, originalIndex: index })
      return acc
    }, {})
  }, [groupedSettings])

  const subjects = Object.keys(groupedBySubject)
  const subjectsWithNotifications = [...subjects, 'Notificaciones'] // agrega la 3ra sección al sidebar
  const isReviewer = (key) => key.includes('AccessControl::Reviewers::')

  // ----- Sección Validaciones (existente) -----
  const handleStatusChange = (groupIndex, itemIndex, value) => {
    setGroupedSettings((prev) => {
      const updated = [...prev]
      updated[groupIndex] = { ...updated[groupIndex] }
      updated[groupIndex].items = [...updated[groupIndex].items]
      updated[groupIndex].items[itemIndex] = {
        ...updated[groupIndex].items[itemIndex],
        status: value,
      }
      return updated
    })
  }

  const handleSensitiveToggle = (groupIndex, itemIndex) => {
    setGroupedSettings((prev) => {
      const updated = [...prev]
      updated[groupIndex] = { ...updated[groupIndex] }
      updated[groupIndex].items = [...updated[groupIndex].items]
      const item = { ...updated[groupIndex].items[itemIndex] }
      item.sensitive = !item.sensitive
      updated[groupIndex].items[itemIndex] = item
      return updated
    })
  }

  const handleSave = async () => {
    const flat = groupedSettings.flatMap((g) => g.items)
    const validStatuses = ['enabled', 'warning', 'disabled']
    const errors = []

    flat.forEach((item, i) => {
      if (typeof item.key !== 'string' || !item.key.includes('AccessControl::')) {
        errors.push(`Ítem ${i + 1}: clave inválida`)
      }
      if (!validStatuses.includes(item.status)) {
        errors.push(`Ítem ${i + 1}: estado inválido (${item.status})`)
      }
      if (typeof item.sensitive !== 'boolean') {
        errors.push(`Ítem ${i + 1}: valor sensible inválido`)
      }
    })

    if (errors.length) {
      alert(`No se puede guardar la configuración:\n\n${errors.join('\n')}`)
      return
    }

    try {
      await axios.post('/api/v1/access_control_settings', { config: flat })
      toast.success('Configuración guardada')
      // nos mantenemos en la página
    } catch {
      alert('Error al guardar la configuración')
    }
  }

  // ------- Notificaciones: helpers -------
  const updateNotifFor = (kind, updater) => {
    setNotifRules(prev => {
      const cur = prev[kind] || {}
      const et = { ...(cur.enabled_targets || {}) }
      const updated = updater({ cur, et })
      return { ...prev, [kind]: updated }
    })
    setNotifDirty(prev => ({ ...prev, [kind]: true }))
  }

  const toggleTarget = (kind, key) => {
    updateNotifFor(kind, ({ cur, et }) => {
      et[key] = !et[key]
      return { ...cur, enabled_targets: et }
    })
  }

  // ⬇️ FIX: guardar los “otros correos” en top-level `other_emails`, no en enabled_targets
  const setOthersText = (kind, text) => {
    const list = text
      .split(/[,\s;]+/)
      .map(s => s.trim())
      .filter(Boolean)

    updateNotifFor(kind, ({ cur, et }) => ({
      ...cur,
      enabled_targets: et,
      other_emails: list
    }))
  }

  const setIncludeParent = (kind, val) => {
    updateNotifFor(kind, ({ cur, et }) => ({
      ...cur,
      enabled_targets: et,
      include_parent_contractor: !!val
    }))
  }

  const currentOthersText = (kind) => {
    const arr = notifRules[kind]?.other_emails || []
    return arr.join(', ')
  }

  // ⬇️ FIX: construir others_text desde rule.other_emails (top-level)
  const buildPayload = (kind) => {
    const rule = notifRules[kind] || {}
    return {
      event: notifEvent,
      subject_kind: kind,
      enabled_targets: rule.enabled_targets || {},
      include_parent_contractor: !!rule.include_parent_contractor,
      others_text: (rule.other_emails || []).join(', ')
    }
  }

  const saveNotifications = async () => {
    setNotifSaving(true)
    try {
      const changedKinds = ['employee', 'asset'].filter(k => notifDirty[k])
      if (changedKinds.length === 0) {
        toast.info('No hay cambios para guardar')
        return
      }

      for (const kind of changedKinds) {
        await axios.post('/api/v1/access_control/notification_rules', buildPayload(kind))
      }

      // Recarga desde servidor para reflejar exactamente lo guardado
      const { data } = await axios.get('/api/v1/access_control/notification_rules', { params: { event: notifEvent } })
      setNotifRules({
        employee: data?.rules?.employee || null,
        asset: data?.rules?.asset || null
      })
      setNotifDirty({ employee: false, asset: false })

      toast.success(
        changedKinds.length === 1
          ? `Notificaciones de ${changedKinds[0] === 'employee' ? 'Persona' : 'Activo'} guardadas`
          : 'Notificaciones guardadas'
      )
    } catch {
      alert('Error al guardar las notificaciones')
    } finally {
      setNotifSaving(false)
    }
  }

  const hasNotifChanges = notifDirty.employee || notifDirty.asset

  // ------- Render -------
  return (
    <PageLayout title="Configuración de Control de Acceso">
      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar */}
        <aside className="col-span-3 border-r-2 h-full pr-2">
          <ul className="space-y-1">
            {[...subjects, 'Notificaciones'].map((subject) => {
              const active = subject === activeSubject
              return (
                <li key={subject}>
                  <button
                    type="button"
                    onClick={() => setActiveSubject(subject)}
                    className={`w-full text-left px-4 py-3 font-medium side-bar__link ${active ? 'bg-blue-100' : ''}`}
                  >
                    {subject}
                  </button>
                </li>
              )
            })}
          </ul>
        </aside>

        {/* Contenido */}
        <section className="col-span-9">
          {/* Secciones existentes (Personas / Activos) */}
          {activeSubject !== 'Notificaciones' && activeSubject &&
            groupedBySubject[activeSubject]?.map(({ group, items, originalIndex }) => {
              const showSensitive = !isReviewer(items[0]?.key)
              return (
                <div key={group} className="mb-6">
                  <h3 className="text-md font-semibold text-gray-700 mb-3">{group}</h3>

                  <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    {/* Header de columnas */}
                    <div className="hidden md:grid md:grid-cols-12 px-4 py-2 bg-gray-50 text-sm text-gray-600 font-medium border-b">
                      <div className="md:col-span-7">Regla</div>
                      <div className="md:col-span-3">Estado</div>
                      {showSensitive && <div className="md:col-span-2 text-center">Sensible</div>}
                    </div>

                    {items.map((item, itemIndex) => (
                      <div
                        key={item.key}
                        className="grid grid-cols-1 md:grid-cols-12 items-center gap-3 px-4 py-3 border-b last:border-b-0"
                      >
                        {/* Col 1: label (7/12) */}
                        <div className="md:col-span-7 font-medium flex items-center gap-2">
                          {isReviewer(item.key) ? (
                            <span className="text-gray-500" title="Revisión de documentos">📄</span>
                          ) : (
                            <span className="text-gray-500" title="Validación">🛡️</span>
                          )}
                          {item.label}
                        </div>

                        {/* Col 2: estado (3/12) */}
                        <div className="md:col-span-3">
                          <Select
                            value={item.status}
                            className="w-full min-w-[160px]"
                            onChange={(value) => handleStatusChange(originalIndex, itemIndex, value)}
                          >
                            <Option value="enabled">Activo</Option>
                            <Option value="warning">Advertencia</Option>
                            <Option value="disabled">Desactivado</Option>
                          </Select>
                        </div>

                        {/* Col 3: sensible (2/12) — sólo para validaciones */}
                        {showSensitive && (
                          <div className="md:col-span-2 flex justify-center">
                            <Switch
                              checked={item.sensitive}
                              onChange={() => handleSensitiveToggle(originalIndex, itemIndex)}
                              disabled={!item.sensitive_editable}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}

          {/* Nueva sección: Notificaciones */}
          {activeSubject === 'Notificaciones' && (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col md:flex-row md:items-end gap-4">
                <div className="w-full md:w-64">
                  <Typography variant="small" className="mb-1">Evento</Typography>
                  <Select value={notifEvent} onChange={(v) => setNotifEvent(v || 'access_reject')}>
                    <Option value="access_reject">Rechazo de acceso</Option>
                    {/* Futuro: agregar más eventos aquí */}
                  </Select>
                </div>
              </div>

              {notifLoading ? (
                <div className="text-sm text-blue-gray-600">Cargando reglas de notificación…</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Card PERSONA */}
                  <div className="bg-white rounded-xl shadow-sm border p-4">
                    <h3 className="text-md font-semibold mb-3">Persona</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {NOTIF_TARGETS.map(t => (
                        <Checkbox
                          key={`employee-${t.key}`}
                          checked={!!notifRules.employee?.enabled_targets?.[t.key]}
                          onChange={() => toggleTarget('employee', t.key)}
                          label={t.label}
                        />
                      ))}
                    </div>

                    <div className="mt-4 flex items-center justify-between border rounded px-3 py-2">
                      <div>
                        <div className="font-medium">Incluir contratista padre</div>
                        <div className="text-xs text-blue-gray-600">
                          Si el sujeto pertenece a una subcontratista, notificar también a la contratista principal.
                        </div>
                      </div>
                      <Switch
                        checked={!!notifRules.employee?.include_parent_contractor}
                        onChange={(e) => setIncludeParent('employee', e.target.checked)}
                      />
                    </div>

                    <div className="mt-4">
                      <Typography variant="small" className="mb-1">Otros correos</Typography>
                      <Textarea
                        rows={3}
                        placeholder="ej: correo1@empresa.com, correo2@otra.com"
                        value={currentOthersText('employee')}
                        onChange={(e) => setOthersText('employee', e.target.value)}
                      />
                      <Typography variant="small" className="text-blue-gray-600 mt-1">
                        Se extraerán automáticamente correos válidos del texto.
                      </Typography>
                    </div>
                  </div>

                  {/* Card ACTIVO */}
                  <div className="bg-white rounded-xl shadow-sm border p-4">
                    <h3 className="text-md font-semibold mb-3">Activo</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {NOTIF_TARGETS.map(t => (
                        <Checkbox
                          key={`asset-${t.key}`}
                          checked={!!notifRules.asset?.enabled_targets?.[t.key]}
                          onChange={() => toggleTarget('asset', t.key)}
                          label={t.label}
                        />
                      ))}
                    </div>

                    <div className="mt-4 flex items-center justify-between border rounded px-3 py-2">
                      <div>
                        <div className="font-medium">Incluir contratista padre</div>
                        <div className="text-xs text-blue-gray-600">
                          Si el sujeto pertenece a una subcontratista, notificar también a la contratista principal.
                        </div>
                      </div>
                      <Switch
                        checked={!!notifRules.asset?.include_parent_contractor}
                        onChange={(e) => setIncludeParent('asset', e.target.checked)}
                      />
                    </div>

                    <div className="mt-4">
                      <Typography variant="small" className="mb-1">Otros correos</Typography>
                      <Textarea
                        rows={3}
                        placeholder="ej: correo1@empresa.com, correo2@otra.com"
                        value={currentOthersText('asset')}
                        onChange={(e) => setOthersText('asset', e.target.value)}
                      />
                      <Typography variant="small" className="text-blue-gray-600 mt-1">
                        Se extraerán automáticamente correos válidos del texto.
                      </Typography>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button variant="text" onClick={() => navigate('/controldeacceso')}>
                  Cancelar
                </Button>
                <Button onClick={saveNotifications} disabled={notifLoading || notifSaving || !hasNotifChanges}>
                  {notifSaving ? 'Guardando…' : hasNotifChanges ? 'Guardar notificaciones' : 'Sin cambios'}
                </Button>
              </div>
            </div>
          )}

          {/* Footer de la página (botones de la sección de validaciones) */}
          {activeSubject !== 'Notificaciones' && (
            <div className="flex justify-end gap-3 mt-10">
              <Button variant="text" onClick={() => navigate('/controldeacceso')}>
                Cancelar
              </Button>
              <Button onClick={handleSave}>Guardar cambios</Button>
            </div>
          )}
        </section>
      </div>
    </PageLayout>
  )
}